import React, { useEffect, useState, useCallback } from "react";
import { getDocs, query, where, orderBy, limit } from "firebase/firestore";
import { dbAnnouncements } from "../firebase";
import { useTheme } from "../contexts/ThemeSelection";
import { useUser } from "../contexts/UserContext";
import useAccentColor from "../hooks/useAccentColor";
import styles from "../styles/WhatsNew.module.css";

interface ChangelogEntry {
  id: string;
  title: string;
  body: string;
  createdAt: Date;
}

const STORAGE_KEY = "onyxlife_lastSeenChangelog";

const getLastSeen = (): number => {
  const val = localStorage.getItem(STORAGE_KEY);
  return val ? Number(val) : 0;
};

const markSeen = () => {
  localStorage.setItem(STORAGE_KEY, String(Date.now()));
};

interface WhatsNewButtonProps {
  onClick: () => void;
  hasUnseen: boolean;
}

export const WhatsNewButton: React.FC<WhatsNewButtonProps> = ({ onClick, hasUnseen }) => {
  const { accent } = useAccentColor();
  return (
    <button
      className={styles.sparkleBtn}
      onClick={onClick}
      style={{ color: accent }}
      title="What's New"
    >
      <i className="fa-solid fa-sparkles" style={{ fontSize: "20px" }} />
      {hasUnseen && (
        <span className={styles.sparkleDot} style={{ backgroundColor: accent }} />
      )}
    </button>
  );
};

interface WhatsNewModalProps {
  show: boolean;
  onClose: () => void;
  entries: ChangelogEntry[];
  lastSeenTimestamp: number;
}

const WhatsNewModal: React.FC<WhatsNewModalProps> = ({ show, onClose, entries, lastSeenTimestamp }) => {
  const { activeTheme, theme } = useTheme();
  const { accent } = useAccentColor();

  useEffect(() => {
    if (show) markSeen();
  }, [show]);

  if (!show) return null;

  const formatDate = (d: Date): string => {
    try {
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch {
      return "";
    }
  };

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div
        className={styles.modal}
        style={{
          backgroundColor: theme[activeTheme].panelColor,
          color: theme[activeTheme].color,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <span className={styles.headerTitle}>
            <i className="fa-solid fa-sparkles" style={{ color: accent }} />
            What's New
          </span>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            style={{ color: theme[activeTheme].color }}
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className={styles.body}>
          {entries.length === 0 ? (
            <div className={styles.emptyState}>No updates yet. Check back soon!</div>
          ) : (
            <div className={styles.entryList}>
              {entries.map((entry) => {
                const isNew = entry.createdAt.getTime() > lastSeenTimestamp;
                return (
                  <div key={entry.id} className={styles.entry}>
                    <div
                      className={styles.entryDot}
                      style={{ backgroundColor: isNew ? accent : "rgba(128,128,128,0.3)" }}
                    />
                    <div className={styles.entryContent}>
                      <div className={styles.entryTitle}>
                        {entry.title}
                        {isNew && (
                          <span
                            className={styles.newBadge}
                            style={{ backgroundColor: accent + "20", color: accent }}
                          >
                            New
                          </span>
                        )}
                      </div>
                      <div className={styles.entryBody}>{entry.body}</div>
                      <div className={styles.entryDate}>{formatDate(entry.createdAt)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/** Hook to fetch changelog entries and track unseen state */
export const useWhatsNew = () => {
  const [entries, setEntries] = useState<ChangelogEntry[]>([]);
  const [lastSeenTimestamp] = useState(getLastSeen);
  const userContext = useUser();
  const uid = userContext?.userDetails?.user?.uid;

  const fetchEntries = useCallback(async () => {
    try {
      const snap = await getDocs(
        query(
          dbAnnouncements,
          where("type", "==", "changelog"),
          where("active", "==", true),
          orderBy("createdAt", "desc"),
          limit(20)
        )
      );
      setEntries(
        snap.docs.map((d) => ({
          id: d.id,
          title: d.data().title || "",
          body: d.data().body || "",
          createdAt: d.data().createdAt?.toDate?.() || new Date(d.data().createdAt),
        }))
      );
    } catch {
      // Collection or index may not exist yet — that's fine
    }
  }, []);

  useEffect(() => {
    if (uid) fetchEntries();
  }, [uid, fetchEntries]);

  const hasUnseen = entries.some((e) => e.createdAt.getTime() > lastSeenTimestamp);

  return { entries, hasUnseen, lastSeenTimestamp };
};

export default WhatsNewModal;
